import { useMemo, type CSSProperties } from "react";
import { NodeAvatar } from "../primitives";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import { useAgentBrainSnapshot, useNeedsYou } from "../../hooks/use-focus-model";
import type { LibraryNode } from "../../../lib/types";

/**
 * Sprint 26.2 — the Team lens on the Workforce route: the agent pyramid.
 *
 * Renders the agent hierarchy (parent → child, derived from the Library tree's
 * `parent_id`) as an indented tree. A sub-agent — including one spawned by a
 * delegation, which is created as a child node — nests under its parent. Each
 * row shows avatar · name · top task, plus a queued count and an amber "needs
 * you" flag so a supervisor can scan the whole org for what's stuck. Click any
 * agent → its Overview.
 */

export interface WorkforceTeamViewProps {
  agents: LibraryNode[];
  onOpen: (nodeId: string) => void;
}

interface TreeNode {
  agent: LibraryNode;
  children: TreeNode[];
}

function buildForest(agents: LibraryNode[]): TreeNode[] {
  const ids = new Set(agents.map((a) => a.id));
  const childrenOf = new Map<string, LibraryNode[]>();
  const roots: LibraryNode[] = [];
  for (const a of agents) {
    if (a.parent_id && ids.has(a.parent_id)) {
      const arr = childrenOf.get(a.parent_id) ?? [];
      arr.push(a);
      childrenOf.set(a.parent_id, arr);
    } else {
      // Parent is the workspace root or a folder — this agent is a top-level hire.
      roots.push(a);
    }
  }
  const byName = (a: LibraryNode, b: LibraryNode) => a.name.localeCompare(b.name);
  const toNode = (agent: LibraryNode): TreeNode => ({
    agent,
    children: (childrenOf.get(agent.id) ?? []).sort(byName).map(toNode),
  });
  return roots.sort(byName).map(toNode);
}

export function WorkforceTeamView({ agents, onOpen }: WorkforceTeamViewProps) {
  const forest = useMemo(() => buildForest(agents), [agents]);

  return (
    <div style={wrap} role="tree" aria-label="Team hierarchy">
      {forest.map((node) => (
        <TeamBranch key={node.agent.id} node={node} depth={0} onOpen={onOpen} />
      ))}
    </div>
  );
}

function TeamBranch({ node, depth, onOpen }: { node: TreeNode; depth: number; onOpen: (id: string) => void }) {
  return (
    <>
      <TeamAgentRow agent={node.agent} depth={depth} hasChildren={node.children.length > 0} onOpen={onOpen} />
      {node.children.map((child) => (
        <TeamBranch key={child.agent.id} node={child} depth={depth + 1} onOpen={onOpen} />
      ))}
    </>
  );
}

function TeamAgentRow({
  agent,
  depth,
  hasChildren,
  onOpen,
}: {
  agent: LibraryNode;
  depth: number;
  hasChildren: boolean;
  onOpen: (id: string) => void;
}) {
  const persona = getAgentPersona({ path: agent.path, agent_config: agent.agent_config });
  const snapshotQuery = useAgentBrainSnapshot(agent.id);
  const needsYouQuery = useNeedsYou(agent.id);

  const snapshot = snapshotQuery.data;
  const topTask = snapshot?.current ?? null;
  const queued = snapshot?.counters?.up_next ?? 0;
  const needsYouCount = needsYouQuery.data?.length ?? 0;
  const role = agent.description?.trim() || "Agent";

  const topTaskText = topTask
    ? topTask.title
    : snapshotQuery.isLoading
      ? "…"
      : "Available";

  return (
    <div
      role="treeitem"
      tabIndex={0}
      aria-label={`${agent.name} — ${role}`}
      style={{ ...row, marginLeft: depth * 22 }}
      onClick={() => onOpen(agent.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(agent.id); } }}
      data-testid="team-agent-row"
    >
      {/* Hierarchy connector for nested rows. */}
      {depth > 0 && <span style={connector} aria-hidden="true" />}

      <NodeAvatar type="agent" persona={persona} name={agent.name} size="sm" />

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={nameRow}>
          <span style={nameText} title={agent.name}>{agent.name}</span>
          {hasChildren && <span style={managesPill} title="Manages sub-agents"><i className="ti ti-sitemap" style={{ fontSize: 11 }} aria-hidden="true" /></span>}
          {needsYouCount > 0 && (
            <span style={needsYouPill} data-testid="needs-you-flag">
              <i className="ti ti-alert-triangle" style={{ fontSize: 11 }} aria-hidden="true" />
              Needs you
            </span>
          )}
        </div>
        <div style={taskText} title={topTaskText}>{topTaskText}</div>
      </div>

      {queued > 0 && (
        <span style={countPill} title={`${queued} queued`}>{queued} queued</span>
      )}
      <i className="ti ti-chevron-right" style={{ fontSize: 14, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
    </div>
  );
}

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  minWidth: 0,
};

const connector: CSSProperties = {
  width: 10,
  height: 10,
  borderLeft: "0.5px solid var(--color-border-secondary)",
  borderBottom: "0.5px solid var(--color-border-secondary)",
  borderBottomLeftRadius: 4,
  flexShrink: 0,
  marginLeft: -2,
  marginTop: -8,
};

const nameRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
};

const nameText: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const taskText: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  marginTop: 1,
};

const managesPill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  color: "var(--color-text-tertiary)",
  flexShrink: 0,
};

const needsYouPill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  flexShrink: 0,
  padding: "1px 7px",
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 500,
  color: "var(--c-amber-600, #b45309)",
  background: "var(--c-amber-100, rgba(245,158,11,0.12))",
  border: "0.5px solid var(--c-amber-300, rgba(245,158,11,0.35))",
};

const countPill: CSSProperties = {
  flexShrink: 0,
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
};
